<script setup lang="ts">
import type { Status } from '@/components/diagrams/BigTrafficLightDiagram.vue';
import type {
  ModelValue as SmallBtnSwitchFieldModelValue,
  Values as SmallBtnSwitchFieldValues,
} from '@/components/fields/SmallBtnSwitchField.vue';

import { computed, ref, toRaw } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';
import _cloneDeep from 'lodash/cloneDeep';

import TableActionBtn from '@/components/btns/TableActionBtn.vue';
import BigTrafficLightDiagram from '@/components/diagrams/BigTrafficLightDiagram.vue';
import SmallBtnSwitchField from '@/components/fields/SmallBtnSwitchField.vue';

interface Props {
  statuses?: Status[];
  timeZones?: SmallBtnSwitchFieldValues;
  downloadShippingLabel?: () => Promise<unknown>;
}
export type TableFoldProps = Props;
const props = withDefaults(defineProps<Props>(), {
  statuses: () => [],
  timeZones: () => ['Australia/Sydney', 'Australia/Sydney'],
  downloadShippingLabel: undefined,
});

const isFetchingData = ref(false);

const timeZoneToggle = ref<SmallBtnSwitchFieldModelValue>(0);

function handleDownloadShippingLabelBtnClick(event: Event): void {
  if (!props.downloadShippingLabel) return;
  if (isFetchingData.value) return;
  isFetchingData.value = true;
  void props.downloadShippingLabel().finally(() => {
    isFetchingData.value = false;
  });
}

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'MMM d, hh:mm a zzz');
}

const datedStatuses = computed(() => {
  const _datedStatuses = _cloneDeep(toRaw(props.statuses));
  if (_datedStatuses[0]?.name) {
    _datedStatuses[0].tooltip = _datedStatuses[0].name;
    _datedStatuses[0].name = formatDate(
      _datedStatuses[0].name,
      props.timeZones[timeZoneToggle.value],
    );
  }
  _datedStatuses.forEach((item) => {
    if (item.list) {
      item.list.forEach((listItem) => {
        if (listItem.value) {
          listItem.tooltip = listItem.value;
          listItem.value = formatDate(
            listItem.value,
            props.timeZones[timeZoneToggle.value],
          );
        }
      });
    }
  });
  return _datedStatuses;
});
</script>

<template>
  <section data-testid="dashboardTableFold" :class="$style.dashboardTableFold">
    <div :class="$style.allTimeAreIn">
      All time are in
      <SmallBtnSwitchField
        v-model="timeZoneToggle"
        :values="props.timeZones"
        :disabled="props.timeZones[0] === props.timeZones[1]"
      />
    </div>
    <BigTrafficLightDiagram :statuses="datedStatuses" />
    <TableActionBtn
      :class="$style.downloadShippingLabelBtn"
      icon="mdi-download-outline"
      :loading="isFetchingData"
      :disabled="!props.downloadShippingLabel"
      @click="handleDownloadShippingLabelBtnClick"
    >
      Shipping label
    </TableActionBtn>
  </section>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dashboardTableFold {
}
  .allTimeAreIn {
    margin-bottom: 16px;
  }
  .downloadShippingLabelBtn {
    margin-top: -28px;
    float: right;
  }
</style>
